import { useEffect, useRef, useState } from "react";
import { Button } from "@legal-500/legal500-design-system";
import type { FirmLogoFillItem } from "../../../api/firmLogoFill.service.js";
import { firmLogosService } from "../../../api/profiles/firmLogosService.js";
import { CandidateCard, type CandidateSelection } from "./CandidateCard.js";
import styles from "./FirmLogoCopy.module.css";

type LogoPickerProps = {
  item: FirmLogoFillItem;
  firmName: string;
  onSaved: () => Promise<void>;
};

function selectionOf(candidateKey: string, selectedKey: string | null): CandidateSelection {
  if (selectedKey === null) {
    return "none";
  }

  return candidateKey === selectedKey ? "selected" : "unselected";
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function saveButtonLabelOf(isSaving: boolean, hasCurrentLogo: boolean): string {
  if (isSaving) {
    return "Saving…";
  }

  return hasCurrentLogo ? "Replace current logo" : "Use selected logo";
}

export function LogoPicker({ item, firmName, onSaved }: LogoPickerProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (saveError !== null) {
      saveButtonRef.current?.focus();
    }
  }, [saveError]);

  const selectedCandidate =
    item.candidates.find(candidate => candidate.logoHash === selectedKey) ?? null;
  const activeKey = selectedCandidate ? selectedKey : null;

  const toggleCandidate = (candidateKey: string) => {
    setSelectedKey(previous => (previous === candidateKey ? null : candidateKey));
    setSaveError(null);
  };

  const saveSelected = async () => {
    if (!selectedCandidate) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      await firmLogosService.set({
        companyId: item.companyId,
        website: item.website,
        countryId: item.countryId,
        logoHash: selectedCandidate.logoHash,
        logoFilename: selectedCandidate.logoFilename,
      });
    } catch (error) {
      setSaveError(messageOf(error));
      setIsSaving(false);
      return;
    }

    setSelectedKey(null);
    await onSaved();
    setIsSaving(false);
  };

  return (
    <div className={styles.picker}>
      <ul className={styles.candidates} aria-label={`${firmName} logos found`}>
        {item.candidates.map((candidate, index) => {
          const position = index + 1;
          const candidateKey = candidate.logoHash;

          return (
            <CandidateCard
              key={candidateKey}
              candidate={candidate}
              position={position}
              firmName={firmName}
              isCurrent={item.currentLogo?.logoHash === candidateKey}
              selection={selectionOf(candidateKey, activeKey)}
            >
              <button
                type="button"
                className={styles.pickButton}
                aria-pressed={candidateKey === activeKey}
                disabled={isSaving}
                onClick={() => toggleCandidate(candidateKey)}
              >
                Choose logo {position}
              </button>
            </CandidateCard>
          );
        })}
      </ul>

      <div className={styles.pickActions}>
        <Button
          ref={saveButtonRef}
          type="button"
          size="small"
          disabled={!selectedCandidate || isSaving}
          onClick={() => void saveSelected()}
        >
          {saveButtonLabelOf(isSaving, item.currentLogo !== null)}
        </Button>
      </div>

      {saveError ? (
        <div className={styles.exportError} role="alert">
          {`The logo couldn't be saved: ${saveError}`}
        </div>
      ) : null}
    </div>
  );
}
